import React from 'react';
import { Box, Typography } from '@mui/material';
import { ActionRow, instructTargets, instructionsForAction, instructionsForBot } from './lib/selectors';
import { Bot, Snapshot } from './lib/types';
import { InstructComposer } from './InstructComposer';
import { InstructionThread } from './InstructionThread';

/** The threads of one bot, newest first, with the one named in the URL (or the first unread) open. */
function Threads({ list, botName, openId }: { list: ReturnType<typeof instructionsForBot>; botName: string; openId: string | null }) {
  const firstUnread = list.find((i) => i.unread);
  const [open, setOpen] = React.useState<Record<string, boolean>>({});
  const isOpen = (id: string) => (id in open ? open[id] : id === openId || (!openId && firstUnread ? firstUnread.id === id : false));
  return (
    <>
      {list.map((i) => (
        <InstructionThread
          key={i.id}
          instruction={i}
          botName={botName}
          expanded={isOpen(i.id)}
          onToggle={() => setOpen((o) => ({ ...o, [i.id]: !isOpen(i.id) }))}
        />
      ))}
    </>
  );
}

/** The Inbox section of the bot drawer: send an instruction or question, and read the threads. */
export function InboxPanel({ snapshot, bot, openId, focusComposer = false }: { snapshot: Snapshot; bot: Bot; openId: string | null; focusComposer?: boolean }) {
  const list = instructionsForBot(snapshot, bot.slug);
  return (
    <Box>
      <InstructComposer
        label={`Message ${bot.name}`}
        kind="instruction"
        kindChoice
        targets={[{ slug: bot.slug, label: bot.name }]}
        placeholder={`Instruct or ask ${bot.short || bot.name}…`}
        autoFocus={focusComposer}
      />
      <Box sx={{ mt: 1.5 }}>
        {list.length === 0
          ? <Typography variant="body2" color="text.secondary" data-testid="inbox-empty">No instructions yet.</Typography>
          : <Threads list={list} botName={bot.name} openId={openId} />}
      </Box>
    </Box>
  );
}

/** "Ask a question" in the recommendation drawer: a question to the owner bot tied to this item, and its thread. */
export function QuestionPanel({ snapshot, row, openId }: { snapshot: Snapshot; row: ActionRow; openId: string | null }) {
  const list = instructionsForAction(snapshot, row.action.id);
  return (
    <Box>
      <InstructComposer
        label={`Ask ${row.bot.name} a question`}
        kind="question"
        relatedActionId={row.action.id}
        targets={instructTargets(snapshot, row.bot)}
        placeholder={`Ask ${row.bot.short || row.bot.name} about this item…`}
        submitLabel="Ask"
      />
      {list.length > 0 && <Box sx={{ mt: 1.5 }}><Threads list={list} botName={row.bot.name} openId={openId} /></Box>}
    </Box>
  );
}
